import { useParams, useHistory, useRouteMatch } from 'react-router-dom';

// Material UI
import { styled, Toolbar, Box, Drawer, Divider as _Divider, IconButton } from '@mui/material';
import { List, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import InfoIcon from '@mui/icons-material/Info';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ReceiptIcon from '@mui/icons-material/Receipt';
import BallotIcon from '@mui/icons-material/Ballot';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import StoreIcon from '@mui/icons-material/Store';
import ShareIcon from '@mui/icons-material/Share';
import BadgeIcon from '@mui/icons-material/Badge';
import { GetApp } from '@mui/icons-material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import Tooltip from 'components/popups/Tooltip';

import { PanelLeftClose, PanelLeftOpen } from 'lucide-react';

// Context
import { useDrawer } from 'context/drawer';

/**
 * Desktop Only
 * See EventTabs.js for the mobile version of this menu
 */

const DRAWER_WIDTH = 240;
const DRAWER_WIDTH_COLLAPSED = 64;

export default function EventDrawer() {
    const history = useHistory();
    const { id } = useParams();
    const { isCollapsed, toggleDrawer } = useDrawer();

    const path = '/events/' + id;

    const dashboard = useRouteMatch({ path: '/events/:id/', exact: true });
    const details = useRouteMatch({ path: '/events/:id/details' });
    const checkout = useRouteMatch({ path: '/events/:id/checkout' });
    const checkoutForm = useRouteMatch({ path: '/events/:id/checkout-form' });
    const tickets = useRouteMatch({ path: '/events/:id/tickets' });
    const discounts = useRouteMatch({ path: '/events/:id/discounts' });
    const publish = useRouteMatch({ path: '/events/:id/publish' });
    const payouts = useRouteMatch({ path: '/events/:id/payouts' });
    const orders = useRouteMatch({ path: '/events/:id/orders' });
    const onsite = useRouteMatch({ path: '/events/:id/onsite', exact: true });
    const downloads = useRouteMatch({ path: '/events/:id/downloads' });
    const reports = useRouteMatch({ path: '/events/:id/reports' });
    const promote = useRouteMatch({ path: '/events/:id/promote' });
    const onsiteRoles = useRouteMatch({ path: '/events/:id/onsite-roles', exact: true });

    const drawerWidth = isCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH;

    return (
        <Box displayPrint="none">
            <Drawer
                variant="permanent"
                sx={{
                    width: drawerWidth,
                    flexShrink: 0,
                    transition: (theme) =>
                        theme.transitions.create('width', {
                            easing: theme.transitions.easing.sharp,
                            duration: isCollapsed
                                ? theme.transitions.duration.leavingScreen
                                : theme.transitions.duration.enteringScreen,
                        }),
                    '& .MuiDrawer-paper': {
                        width: drawerWidth,
                        transition: (theme) =>
                            theme.transitions.create('width', {
                                easing: theme.transitions.easing.sharp,
                                duration: isCollapsed
                                    ? theme.transitions.duration.leavingScreen
                                    : theme.transitions.duration.enteringScreen,
                            }),
                        overflowX: 'hidden',
                    },
                }}
            >
                <Toolbar />

                <DrawerContainer>
                    <List sx={{ flexGrow: 1 }}>
                        <MenuItem
                            title="Dashboard"
                            icon={<DashboardIcon />}
                            active={Boolean(dashboard)}
                            onClick={() => history.push(`${path}/`)}
                            isCollapsed={isCollapsed}
                        />

                        <Divider />

                        <MenuItem
                            title="Event Details"
                            icon={<InfoIcon />}
                            active={Boolean(details)}
                            onClick={() => history.push(`${path}/details`)}
                            isCollapsed={isCollapsed}
                        />

                        <MenuItem
                            title="Tickets"
                            icon={<ConfirmationNumberIcon />}
                            active={Boolean(tickets)}
                            onClick={() => history.push(`${path}/tickets`)}
                            isCollapsed={isCollapsed}
                        />

                        <MenuItem
                            title="Discounts"
                            icon={<LocalOfferIcon />}
                            active={Boolean(discounts)}
                            onClick={() => history.push(`${path}/discounts`)}
                            isCollapsed={isCollapsed}
                        />

                        <MenuItem
                            title="Customize Checkout"
                            icon={<ShoppingCartIcon />}
                            active={Boolean(checkout)}
                            onClick={() => history.push(`${path}/checkout`)}
                            isCollapsed={isCollapsed}
                        />

                        <MenuItem
                            title="Checkout Form"
                            icon={<BallotIcon />}
                            active={Boolean(checkoutForm)}
                            onClick={() => history.push(`${path}/checkout-form`)}
                            isCollapsed={isCollapsed}
                        />

                        {process.env.REACT_APP_ENV === 'development' && (
                            <MenuItem
                                title="Onsite Roles"
                                icon={<BadgeIcon />}
                                active={Boolean(onsiteRoles)}
                                onClick={() => history.push(`${path}/onsite-roles`)}
                                isCollapsed={isCollapsed}
                            />
                        )}

                        <Divider />

                        <MenuItem
                            title="Review & Publish"
                            icon={<StoreIcon />}
                            active={Boolean(publish)}
                            onClick={() => history.push(`${path}/publish`)}
                            isCollapsed={isCollapsed}
                        />

                        <Divider />

                        <MenuItem
                            title="Sales & Payout"
                            icon={<AccountBalanceIcon />}
                            active={Boolean(payouts)}
                            onClick={() => history.push(`${path}/payouts`)}
                            isCollapsed={isCollapsed}
                        />

                        <MenuItem
                            title="Online Orders"
                            icon={<ReceiptIcon />}
                            active={Boolean(orders)}
                            onClick={() => history.push(`${path}/orders`)}
                            isCollapsed={isCollapsed}
                        />

                        <MenuItem
                            title="In-Person Orders"
                            icon={<ReceiptIcon />}
                            active={Boolean(onsite)}
                            onClick={() => history.push(`${path}/onsite`)}
                            isCollapsed={isCollapsed}
                        />

                        <MenuItem
                            title="Reports"
                            icon={<AssessmentIcon />}
                            active={Boolean(reports)}
                            onClick={() => history.push(`${path}/reports`)}
                            isCollapsed={isCollapsed}
                        />
                        <MenuItem
                            title="Downloads"
                            icon={<GetApp />}
                            active={Boolean(downloads)}
                            onClick={() => history.push(`${path}/downloads`)}
                            isCollapsed={isCollapsed}
                        />

                        <Divider />

                        <MenuItem
                            title="Promote"
                            icon={<ShareIcon />}
                            active={Boolean(promote)}
                            onClick={() => history.push(`${path}/promote`)}
                            isCollapsed={isCollapsed}
                        />
                    </List>

                    <Box
                        sx={{
                            borderTop: 1,
                            borderColor: (theme) => theme.palette.grey[700],
                            display: 'flex',
                            justifyContent: isCollapsed ? 'center' : 'flex-end',
                            px: 2,
                            py: 1,
                        }}
                    >
                        <Tooltip message={isCollapsed ? 'Expand menu' : 'Collapse menu'}>
                            <IconButton
                                onClick={toggleDrawer}
                                sx={{
                                    color: (theme) => theme.palette.common.white,
                                }}
                            >
                                {isCollapsed ? <PanelLeftOpen /> : <PanelLeftClose />}
                            </IconButton>
                        </Tooltip>
                    </Box>
                </DrawerContainer>
            </Drawer>
        </Box>
    );
}

function MenuItem(props) {
    const { active, onClick, icon, title, isCollapsed } = props;

    const IconComponent = active ? ListItemIconActive : ListItemIconInactive;
    const TextComponent = active ? ListItemTextActive : ListItemTextInactive;

    return (
        <ListItemButton
            onClick={active ? undefined : onClick}
            sx={{
                paddingLeft: 2.5,
                paddingRight: 2,
                justifyContent: isCollapsed ? 'center' : 'flex-start',
                '&:hover': {
                    backgroundColor: (theme) => theme.palette.grey[700],
                },
            }}
        >
            <IconComponent sx={{ minWidth: isCollapsed ? 'auto' : 36 }}>{icon}</IconComponent>
            <TextComponent
                primary={title}
                sx={{
                    opacity: isCollapsed ? 0 : 1,
                    whiteSpace: 'nowrap',
                    transition: (theme) =>
                        theme.transitions.create('opacity', {
                            easing: theme.transitions.easing.sharp,
                            duration: theme.transitions.duration.shorter,
                        }),
                }}
            />
        </ListItemButton>
    );
}

const DrawerContainer = styled('div')(({ theme }) => ({
    overflowX: 'hidden',
    overflowY: 'auto',
    backgroundColor:
        process.env.REACT_APP_ENV === 'development'
            ? theme.palette.grey[600]
            : theme.palette.background.dark,
    height: '100%',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
}));

const ListItemIconActive = styled(ListItemIcon)(({ theme }) => ({
    color: theme.palette.primary.main,
    '& svg': {
        fontSize: '1.4rem',
    },
}));

const ListItemTextActive = styled(ListItemText)(({ theme }) => ({
    color: theme.palette.primary.main,
    '& .MuiListItemText-primary': {
        fontSize: '0.9rem',
    },
}));

const ListItemIconInactive = styled(ListItemIcon)(({ theme }) => ({
    color: theme.palette.common.white,
    '& svg': {
        fontSize: '1.4rem',
    },
}));

const ListItemTextInactive = styled(ListItemText)(({ theme }) => ({
    color: theme.palette.common.white,
    '& .MuiListItemText-primary': {
        fontSize: '0.9rem',
    },
}));

const Divider = styled(_Divider)(({ theme }) => ({
    backgroundColor: theme.palette.grey[100],
}));
